import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import * as z from 'zod'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { IUserDetails } from '../_data/user-details-interface'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getOrganizationUserDetailOrganizationOrganizationIdUsersUseruuidGetQueryKey,
  updateOrganizationUserOrganizationOrganizationIdUsersUseruuidPutMutation,
} from '@/generated/@tanstack/react-query.gen'
import { useUser } from '@/pages/enterprise/user-provider'
import { cn } from '@/lib/utils'
import { toast } from '@/components/custom-toast'
import { isArray } from 'lodash'

const formSchema = z.object({
  firstname: z.string().min(2, { message: 'First name is required.' }),
  lastname: z.string().min(2, { message: 'Last name is required.' }),
  email: z.string().email({ message: 'Invalid email address.' }),
  role: z.string(),
})

interface IProps {
  onEditing: (value: boolean) => void
  defaultValues: IUserDetails
  id: string
  isCurrentUser: boolean
}

export default function EditUserForm(props: IProps) {
  const { organizationId } = useUser()
  const { onEditing, isCurrentUser } = props

  const queryClient = useQueryClient()

  const mutate = useMutation({
    ...updateOrganizationUserOrganizationOrganizationIdUsersUseruuidPutMutation(),
    onError: (error) => {
      if (
        error.response?.status === 422 &&
        isArray(error.response.data?.detail)
      ) {
        return (error.response.data?.detail ?? []).forEach((error: any) => {
          form.setError(error.loc[1], {
            type: 'manual',
            message: error.msg,
          })
        })
      }
      toast.error({
        message:
          (error.response?.data.detail as unknown as string) ?? error.message,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey:
          getOrganizationUserDetailOrganizationOrganizationIdUsersUseruuidGetQueryKey(
            {
              path: { organization_id: organizationId, useruuid: props.id },
            }
          ),
      })
      onEditing(false)
      toast.success({
        title: 'Profile Updated',
        message: 'User profile was successfully updated.',
      })
    },
  })

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      ...props.defaultValues,
      firstname: props.defaultValues.firstName,
      lastname: props.defaultValues.lastName,
      role: props.defaultValues.role.toString(),
    },
  })

  function onSubmit(values: z.infer<typeof formSchema>) {
    mutate.mutate({
      path: {
        organization_id: organizationId,
        useruuid: props.id,
      },
      body: {
        firstname: values.firstname,
        lastname: values.lastname,
        email: values.email,
        useraccesslevel: isCurrentUser ? 7 : (parseInt(values.role) as 6 | 7),
      },
    })
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-y-6 gap-x-8 md:max-w-[70%]">
          <FormField
            control={form.control}
            name="firstname"
            render={({ field }) => (
              <FormItem>
                <FormLabel>First Name</FormLabel>
                <FormControl>
                  <Input {...field} className="h-12 max-w-[400px]" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="lastname"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Last Name</FormLabel>
                <FormControl>
                  <Input {...field} className="h-12 max-w-[400px]" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    type="email"
                    className="h-12 max-w-[400px]"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="role"
            render={({ field }) => (
              <FormItem className={cn({ hidden: isCurrentUser })}>
                <FormLabel>Role</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger className="h-12 max-w-[400px] ring-[0.5px] ring-[#D8D8D8]">
                      <SelectValue placeholder="Select a role" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem className="rounded-lg" value="6">
                      Enterprise Member
                    </SelectItem>
                    <SelectItem className="rounded-lg" value="7">
                      Enterprise Admin
                    </SelectItem>
                  </SelectContent>
                </Select>
              </FormItem>
            )}
          />
        </div>
        <div className="flex flex-col md:flex-row gap-4 items-center w-full md:w-auto">
          <Button
            type="submit"
            disabled={mutate.isPending}
            className="w-full md:w-auto text-white px-8 py-3 rounded-md"
          >
            Save Changes
          </Button>
          <Button
            onClick={() => onEditing(false)}
            type="button"
            variant="ghost"
            className="w-full md:w-auto text-primary hover:text-emerald-500 hover:bg-transparent"
          >
            Cancel
          </Button>
        </div>
      </form>
    </Form>
  )
}
